<i18n
  src="@/locales/views/agro_exporter/finances/container_incomes/new_export_invoice.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/export_invoice.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_container')"
    :params="{
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id
    }"
  />

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_general_data">
    <div class="mt-8">
      <container-info
        :agro_exporter_id="agro_exporter_id"
        :container="container"
      />
    </div>

    <alert-danger class="mt-6" :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_code')]">
                  {{ $t("model.export_invoice.invoice_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="export_invoice.invoice_code"
                    :class="['input-text', isInvalid('invoice_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('invoice_date')]">
                  {{ $t("model.export_invoice.invoice_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="export_invoice.invoice_date" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('amount')]">
                  {{ $t("model.export_invoice.amount") }}
                </label>
                <div class="mt-1 flex w-full">
                  <select-form
                    class="w-1/3"
                    :options="currencies"
                    v-model="export_invoice.currency_id"
                    :disabled="currencies.length == 1"
                    :is_invalid="isInvalidSelect('currency')"
                  ></select-form>
                  <div class="flex flex-1 relative">
                    <div
                      class="flex items-center absolute inset-y-0 left-0 ml-3 text-sm leading-5"
                    >
                      {{ currency_symbol }}
                    </div>
                    <input
                      type="text"
                      v-model="export_invoice.amount"
                      :class="['input-text pl-6', isInvalid('amount')]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowContainer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="createExportInvoice()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import moment from "moment";
import Requester from "@/views/agro_exporter/requesters/finances/container_incomes/manage_export_invoice.js";
import ContainerInfo from "@/components/views/agro_exporter/finances/container_incomes/ContainerInfo.vue";

export default {
  name: "AEFinancesContainerIncomesNewExportInvoice",
  components: { ContainerInfo },
  props: ["agro_exporter_process_id", "agro_exporter_id", "container_id"],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      agro_exporter_process: {},
      container: {},
      currencies: [],
      export_invoice: {
        invoice_code: "",
        amount: "",
        invoice_date: "",
        container_id: this.container_id,
        currency_id: ""
      }
    };
  },
  methods: {
    fetchData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchContainer();
      await this.fetchCurrencies();
      this.load_general_data = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.agro_exporter_id,
        this.container_id
      );
      this.container = response.data;
    },
    fetchCurrencies: async function () {
      let response = await Requester.fetchCurrencies(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.currencies = response.data;
      if (this.currencies.length == 1)
        this.export_invoice.currency_id = this.currencies[0].id;
    },
    createExportInvoice: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.create(this.agro_exporter_id, {
          invoice_date: this.invoice_date_formatted,
          invoice_code: this.export_invoice.invoice_code,
          amount: this.export_invoice.amount,
          container_id: this.export_invoice.container_id,
          currency_id: this.export_invoice.currency_id
        });
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowContainer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowContainer: function () {
      this.$router.push({
        name: this.routeName("show_container"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id
        }
      });
    },
    goToShowExportInvoice: function (export_invoice_id) {
      this.$router.push({
        name: this.routeName("show_export_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id,
          export_invoice_id: export_invoice_id
        }
      });
    }
  },
  computed: {
    invoice_date_formatted: function () {
      return moment(this.export_invoice.invoice_date).format("YYYY-MM-DD");
    },
    currency_symbol: function () {
      return this.currencies.find(
        currency => currency.id == this.export_invoice.currency_id
      )?.symbol;
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.fetchData();
      })
    );
  }
};
</script>
